import { useCallback, useEffect, useRef, useState } from "react";

type CopyState = "idle" | "copied" | "error";

interface UseCopyToClipboardOptions {
  resetAfterMs?: number;
  onError?: (error: unknown) => void;
}

interface UseCopyToClipboardResult {
  state: CopyState;
  isIdle: boolean;
  isCopied: boolean;
  isError: boolean;
  copy: (text: string) => Promise<boolean>;
  reset: () => void;
}

const DEFAULT_RESET_MS = 2000;

export function useCopyToClipboard(
  options: UseCopyToClipboardOptions = {},
): UseCopyToClipboardResult {
  const { resetAfterMs = DEFAULT_RESET_MS, onError } = options;
  const [state, setState] = useState<CopyState>("idle");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const scheduleReset = useCallback(() => {
    clearTimer();
    timeoutRef.current = setTimeout(() => setState("idle"), resetAfterMs);
  }, [clearTimer, resetAfterMs]);

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  const reset = useCallback(() => {
    clearTimer();
    setState("idle");
  }, [clearTimer]);

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
        setState("copied");
        scheduleReset();
        return true;
      } catch (error) {
        setState("error");
        scheduleReset();
        onError?.(error);
        return false;
      }
    },
    [onError, scheduleReset],
  );

  return {
    state,
    isIdle: state === "idle",
    isCopied: state === "copied",
    isError: state === "error",
    copy,
    reset,
  };
}
